/* AppraisalComponents/SpoutingBracketSummary.jsx
 * Phase C · C4e Spouting extraction (2026-02c).
 * Extracted verbatim from AppraisalComponentsTab.js:4161-4620.
 * Spouting section body — bucket summary + Push-to-BOM + collapsible
 * "Edit consumable rates" panel for the office to tune supplier-specific
 * rivet / silicone / dropper rates without leaving the appraisal.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { isMitreLine, isGutterLengthLine } from './helpers';

export function SpoutingBracketSummary({ api, appraisalId, lines, gutterCtx, rates, onPushToBom, onConfigPatched }) {
  // Iter150-D — collapsible "Edit consumable rates" panel. Inline form
  // PATCHes the appraisal's gutter-builder config so the office can
  // tune supplier-specific rates without leaving the appraisal.
  const [showRateEditor, setShowRateEditor] = useState(false);
  const [savingRates, setSavingRates] = useState(false);
  if (!gutterCtx) {
    return (
      <div className="rounded-md border border-cyan-200 bg-cyan-50/30 px-3 py-2 text-xs text-slate-500">
        Bracket summary will appear once the Gutter Details bar above is loaded.
      </div>
    );
  }
  const spanM       = Number(gutterCtx.bracketSpanM) || 0.7;
  const mitreExtraM = (Number(gutterCtx.mitreExtraMm) || 300) / 1000;
  const bufferPct   = Number(gutterCtx.bracketBufferPct) || 0;
  const finish      = gutterCtx.bracketFinish === 'colour' ? 'Painted' : 'Plain';
  // Iter150-D — Read consumable rates from context (gutter-builder
  // config) so the office can tune per appraisal. Defaults match the
  // operator's stated rule (4 rivets + 10% sil / dropper, 5 + 15% per
  // pair of mitres, 4 + 15% per stop end).
  const cnsDropperR    = Number(gutterCtx.dropperRivets ?? 4);
  const cnsDropperPct  = Number(gutterCtx.dropperSiliconePct ?? 10);
  const cnsMitrePairR  = Number(gutterCtx.mitrePairRivets ?? 5);
  const cnsMitrePairPct= Number(gutterCtx.mitrePairSiliconePct ?? 15);
  const cnsStopEndR    = Number(gutterCtx.stopEndRivets ?? 4);
  const cnsStopEndPct  = Number(gutterCtx.stopEndSiliconePct ?? 15);
  // Iter155 — buffer percentages for rivet + silicone totals.
  const rivetBufferPct    = Number(gutterCtx.rivetBufferPct ?? 10);
  const siliconeBufferPct = Number(gutterCtx.siliconeBufferPct ?? 10);
  const perMitreR      = cnsMitrePairR / 2;
  const perMitrePct    = cnsMitrePairPct / 2;

  // Persist any single rate edit to the backend config. Backend's
  // PUT endpoint rebuilds the config from the request body so we must
  // GET the current config first, merge the patch in, then PUT the
  // full object — otherwise unrelated fields (gutter_type / profile /
  // etc.) would reset to defaults.
  const patchRate = async (field, value) => {
    if (!api || !appraisalId) return;
    setSavingRates(true);
    try {
      const { data: current } = await api.get(`/guttering/appraisals/${appraisalId}/gutter-builder/config`);
      const merged = { ...(current || {}), [field]: value };
      const { data } = await api.put(`/guttering/appraisals/${appraisalId}/gutter-builder/config`, merged);
      onConfigPatched?.(data?.config || merged);
    } catch (e) {
      toast.error(`Failed to save consumable rate: ${e?.response?.data?.detail || e.message || e}`);
    } finally {
      setSavingRates(false);
    }
  };

  // Helper — resolve the row's effective mitre count, falling back to
  // the legacy [MITRE] notes tag = count of 1 when the new field is
  // absent (preserves iter149 saved data).
  const rowMitreCount = (l) => {
    const explicit = Number(l.mitre_count);
    if (Number.isFinite(explicit) && explicit > 0) return Math.min(2, explicit);
    return isMitreLine(l) ? 1 : 0;
  };

  // Bucket by type_name. Per-bucket counts + per-outlet-size sub-bucket
  // for downstream Push-to-BOM (one dropper auto-row per outlet size).
  const buckets = new Map();
  (lines || []).forEach(l => {
    if (!isGutterLengthLine(l)) return;  // gutter runs only — outlets / accessories carry no brackets or ends
    const tn = String(l.type_name || '').trim();
    if (!tn) return;
    const baseLen = Number(l.length) > 0
      ? Number(l.quantity) * Number(l.length)
      : Number(l.quantity);
    if (!buckets.has(tn)) {
      buckets.set(tn, {
        rows: 0, base_m: 0, mitre_count: 0, stop_l: 0, stop_r: 0,
        outlet_qty: 0, outlets_by_size: {}, bracket_raw: 0,
      });
    }
    const b = buckets.get(tn);
    b.rows += 1;
    b.base_m += baseLen;
    // Same rule as the Gutter Builder: per run, ceil(base length ÷ span); mitre stubs add no brackets.
    b.bracket_raw += baseLen > 0 ? Math.ceil(baseLen / spanM - 1e-9) : 0;
    b.mitre_count += rowMitreCount(l);
    if (l.stop_end_left)  b.stop_l += 1;
    if (l.stop_end_right) b.stop_r += 1;
    const oqty = Math.max(0, Number(l.outlet_qty) || 0);
    if (oqty > 0 && l.outlet_size) {
      b.outlet_qty += oqty;
      b.outlets_by_size[l.outlet_size] = (b.outlets_by_size[l.outlet_size] || 0) + oqty;
    }
  });

  const groups = Array.from(buckets.entries())
    .map(([type_name, b]) => {
      const stop_ends    = b.stop_l + b.stop_r;
      const mitre_add_m  = b.mitre_count * mitreExtraM;
      const ordered_m    = b.base_m + mitre_add_m;
      const bracket_raw  = b.bracket_raw;
      const bracket_qty  = bufferPct > 0
        ? Math.ceil(bracket_raw * (1 + bufferPct / 100))
        : bracket_raw;
      const buffer_amt   = bracket_qty - bracket_raw;
      // Consumables — per-group floats, ceil at grand total. Uses
      // editable rates from gutter-builder config (iter150-D).
      const rivets_f   = cnsDropperR * b.outlet_qty + cnsStopEndR * stop_ends + perMitreR * b.mitre_count;
      const silicone_f = (cnsDropperPct / 100) * b.outlet_qty + (cnsStopEndPct / 100) * stop_ends + (perMitrePct / 100) * b.mitre_count;
      return { type_name, ...b, stop_ends, mitre_add_m, ordered_m, bracket_raw, bracket_qty, buffer_amt, rivets_f, silicone_f };
    })
    .sort((a, b) => b.base_m - a.base_m);

  const grand = groups.reduce((acc, g) => ({
    rows: acc.rows + g.rows,
    base_m: acc.base_m + g.base_m,
    mitre_count: acc.mitre_count + g.mitre_count,
    mitre_add_m: acc.mitre_add_m + g.mitre_add_m,
    stop_ends: acc.stop_ends + g.stop_ends,
    stop_l: acc.stop_l + g.stop_l,
    stop_r: acc.stop_r + g.stop_r,
    outlet_qty: acc.outlet_qty + g.outlet_qty,
    // Iter150-C — also accumulate dropper qty per outlet size for the
    // grand-total consumables strip + the Push-to-BOM auto-rows.
    outlets_by_size: (() => {
      const next = { ...acc.outlets_by_size };
      Object.entries(g.outlets_by_size || {}).forEach(([k, v]) => { next[k] = (next[k] || 0) + v; });
      return next;
    })(),
    ordered_m: acc.ordered_m + g.ordered_m,
    bracket_raw: acc.bracket_raw + g.bracket_raw,
    bracket_qty: acc.bracket_qty + g.bracket_qty,
    buffer_amt:  acc.buffer_amt + g.buffer_amt,
    rivets_f: acc.rivets_f + g.rivets_f,
    silicone_f: acc.silicone_f + g.silicone_f,
  }), { rows: 0, base_m: 0, mitre_count: 0, mitre_add_m: 0, stop_ends: 0, stop_l: 0, stop_r: 0, outlet_qty: 0, outlets_by_size: {}, ordered_m: 0, bracket_raw: 0, bracket_qty: 0, buffer_amt: 0, rivets_f: 0, silicone_f: 0 });

  if (groups.length === 0) {
    return (
      <div data-testid="spouting-bracket-summary-empty" className="rounded-md border border-dashed border-cyan-200 bg-cyan-50/20 px-3 py-2 text-xs text-slate-500 italic">
        Pick a gutter Type on at least one Spouting row to see the running totals + bracket + consumables calculator.
      </div>
    );
  }

  // Find a Spouting catalogue rate whose name contains "Bracket" + the
  // configured finish (Plain / Painted). Keeps Push-to-BOM idempotent —
  // same gutter context always finds the same rate row.
  const findBracketRate = () => {
    const wantPainted = finish === 'Painted';
    const candidates = (rates || []).filter(r => /bracket/i.test(r.type_name || ''));
    if (!candidates.length) return null;
    const matchFinish = candidates.find(r => {
      const tn = String(r.type_name || '').toLowerCase();
      return wantPainted ? /paint|colour|color/.test(tn) : /plain|galv|zinc/.test(tn);
    });
    return matchFinish || candidates[0];
  };
  const bracketRate = findBracketRate();
  const canPush = !!bracketRate && grand.bracket_qty > 0;
  // Iter155 — rivet + silicone buffer math.
  //   Rivets:   final = ceil(raw × (1 + buffer/100))
  //   Silicone: ceil(raw) then nudge UP one more tube when the
  //             fractional remainder of the raw float is ≥ (1 - buffer/100).
  //             E.g. buffer=10% + raw=2.95 → frac 0.95 ≥ 0.9 → +1 tube = 4.
  const rivetsRaw      = grand.rivets_f;
  const totalRivets    = rivetsRaw > 0
    ? Math.ceil(rivetsRaw * (1 + (rivetBufferPct || 0) / 100))
    : 0;
  const rivetBufferAdd = Math.max(0, totalRivets - Math.ceil(rivetsRaw));
  const siliconeRaw    = grand.silicone_f;
  const siliconeCeil   = Math.ceil(siliconeRaw);
  const siliconeFrac   = siliconeRaw - Math.floor(siliconeRaw);
  const nudgeThreshold = 1 - (siliconeBufferPct || 0) / 100;
  const siliconeNudge  = siliconeRaw > 0 && siliconeFrac > 0 && siliconeFrac >= nudgeThreshold ? 1 : 0;
  const totalSilicone  = siliconeCeil + siliconeNudge;

  return (
    <div data-testid="spouting-bracket-summary" className="rounded-md border border-cyan-300 bg-gradient-to-br from-cyan-50/60 to-white">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-cyan-200 bg-cyan-100/60">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-900">Bracket + consumables summary</span>
        <span className="text-[10px] text-cyan-800">Span <strong className="font-mono-num">{Math.round(spanM * 1000)}mm</strong></span>
        <span className="text-[10px] text-cyan-800">Mitre extra <strong className="font-mono-num">{Math.round(mitreExtraM * 1000)}mm</strong></span>
        <span className="text-[10px] text-cyan-800">Buffer <strong className="font-mono-num">{bufferPct}%</strong></span>
        <span className="text-[10px] text-cyan-800">Finish <strong>{finish}</strong></span>
        <div className="ml-auto">
          <button
            type="button"
            data-testid="spouting-bracket-push-bom"
            disabled={!canPush}
            onClick={() => onPushToBom({ groups, grand, bracketRate, finish, spanM, bufferPct, mitreExtraM, totalRivets, totalSilicone, rivetBufferPct, siliconeBufferPct, rivetBufferAdd, siliconeNudge, siliconeRaw, rivetsRaw })}
            title={canPush
              ? `Create / update auto-tagged Spouting bracket rows for ${groups.length} gutter type${groups.length === 1 ? '' : 's'} — total ${grand.bracket_qty} brackets + ${totalRivets} rivets + ${totalSilicone} silicone tubes.`
              : (!bracketRate
                ? 'No "Bracket" rate row found in the Spouting catalogue — add one in /component-rates first.'
                : 'Total bracket qty is 0 — nothing to push.')}
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
              canPush
                ? 'bg-cyan-600 text-white border-cyan-700 hover:bg-cyan-700'
                : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
            }`}
          >
            ↑ Push to BOM
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-cyan-50/80 text-cyan-900">
              <th className="text-left px-3 py-1.5 font-bold uppercase tracking-wider text-[10px]">Gutter type</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Rows</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Base m</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]" title="Total mitre count across rows in this gutter type">Mitres</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]" title="Left-hand stop ends">S-L</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]" title="Right-hand stop ends">S-R</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]" title="Outlet / dropper count">Outlets</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">+ Mitre m</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Ordered m</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Raw</th>
              <th className="text-right px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">+ Buf</th>
              <th className="text-right px-3 py-1.5 font-bold uppercase tracking-wider text-[10px]">Brackets</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => (
              <tr key={g.type_name} data-testid={`spouting-bracket-row-${g.type_name.replace(/\W+/g, '_').toLowerCase()}`} className="border-t border-cyan-100">
                <td className="px-3 py-1.5 text-slate-800 font-medium">{g.type_name}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-slate-700">{g.rows}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-slate-700">{g.base_m.toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-slate-700">{g.mitre_count || '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-indigo-700">{g.stop_l || '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-indigo-700">{g.stop_r || '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-amber-700">{g.outlet_qty || '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-slate-500">{g.mitre_add_m > 0 ? `+${g.mitre_add_m.toFixed(2)}` : '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-slate-900 font-semibold">{g.ordered_m.toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-slate-700">{g.bracket_raw}</td>
                <td className="px-2 py-1.5 text-right font-mono-num text-emerald-700">{g.buffer_amt > 0 ? `+${g.buffer_amt}` : '—'}</td>
                <td className="px-3 py-1.5 text-right font-mono-num text-cyan-900 font-extrabold">{g.bracket_qty}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-cyan-100/80 border-t-2 border-cyan-300">
              <td className="px-3 py-1.5 font-bold uppercase tracking-wider text-[10px] text-cyan-900">Total</td>
              <td className="px-2 py-1.5 text-right font-mono-num font-bold text-cyan-900">{grand.rows}</td>
              <td className="px-2 py-1.5 text-right font-mono-num font-bold text-cyan-900">{grand.base_m.toFixed(2)}</td>
              <td className="px-2 py-1.5 text-right font-mono-num font-bold text-cyan-900">{grand.mitre_count || '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono-num font-bold text-indigo-800">{grand.stop_l || '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono-num font-bold text-indigo-800">{grand.stop_r || '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono-num font-bold text-amber-800">{grand.outlet_qty || '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono-num text-cyan-800">{grand.mitre_add_m > 0 ? `+${grand.mitre_add_m.toFixed(2)}` : '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono-num font-bold text-cyan-900">{grand.ordered_m.toFixed(2)}</td>
              <td className="px-2 py-1.5 text-right font-mono-num font-bold text-cyan-900">{grand.bracket_raw}</td>
              <td className="px-2 py-1.5 text-right font-mono-num font-bold text-emerald-700">{grand.buffer_amt > 0 ? `+${grand.buffer_amt}` : '—'}</td>
              <td className="px-3 py-1.5 text-right font-mono-num font-extrabold text-cyan-900 text-sm" data-testid="spouting-bracket-grand-qty">{grand.bracket_qty}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {/* Iter150 / 150-C — Consumables strip. Rivet + silicone totals
          roll up across all gutter-type groups using the operator spec:
          4 rivets + 10% silicone per dropper, 5 rivets + 15% silicone
          per pair of mitres (2.5 rivets + 7.5% per mitre), 4 rivets +
          15% silicone per stop end. Iter150-C — droppers are broken out
          per outlet size (80mm / 65mm / Rect) and stop ends are broken
          out per side (L / R) so the procurement-side reader can see
          exactly how many of each product to order. */}
      <div data-testid="spouting-consumables-strip" className="border-t border-cyan-200 bg-amber-50/40 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-900">Consumables</span>

        {/* Droppers — one chip per outlet size that has stock */}
        {grand.outlet_qty === 0 ? (
          <span className="text-slate-400 italic">Droppers —</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded border border-amber-200 bg-white">
            <span className="text-[9px] uppercase font-bold tracking-wider text-amber-900">Droppers</span>
            {['80mm', '65mm', 'rect'].map(sz => {
              const qty = grand.outlets_by_size[sz] || 0;
              if (qty === 0) return null;
              const label = sz === 'rect' ? 'Rect' : sz;
              return (
                <span key={sz} data-testid={`spouting-consum-dropper-${sz}`} className="font-mono-num text-amber-900">
                  <strong>{label}</strong> × {qty}
                </span>
              );
            })}
            <span className="text-[9px] text-slate-500">ea {cnsDropperR}r · {cnsDropperPct}% sil</span>
          </span>
        )}

        {/* Mitres */}
        <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded border border-cyan-200 bg-white">
          <span className="text-[9px] uppercase font-bold tracking-wider text-cyan-900">Mitres</span>
          <strong className="font-mono-num text-cyan-900" data-testid="spouting-consum-mitres">{grand.mitre_count}</strong>
          <span className="text-[9px] text-slate-500">ea {perMitreR}r · {perMitrePct}% sil</span>
        </span>

        {/* Stop ends — L / R broken out */}
        <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded border border-indigo-200 bg-white">
          <span className="text-[9px] uppercase font-bold tracking-wider text-indigo-900">Stop ends</span>
          <span className="font-mono-num text-indigo-900" data-testid="spouting-consum-stop-l">
            <strong>L</strong> × {grand.stop_l}
          </span>
          <span className="font-mono-num text-indigo-900" data-testid="spouting-consum-stop-r">
            <strong>R</strong> × {grand.stop_r}
          </span>
          <span className="text-[9px] text-slate-500">ea {cnsStopEndR}r · {cnsStopEndPct}% sil</span>
        </span>

        <button
          type="button"
          data-testid="spouting-consum-edit-rates"
          onClick={() => setShowRateEditor(s => !s)}
          title="Edit the rivet + silicone rates per dropper / mitre / stop end. Saved per-appraisal in the gutter-builder config."
          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border transition-colors ${
            showRateEditor ? 'bg-amber-200 text-amber-900 border-amber-400' : 'bg-white text-slate-500 border-slate-300 hover:border-amber-300 hover:text-amber-800'
          }`}
        >
          ⚙ rates
        </button>

        <span className="ml-auto text-slate-800">
          Total:
          <strong className="font-mono-num text-amber-900 ml-1" data-testid="spouting-rivet-total">{totalRivets}</strong>
          {' '}rivets{rivetBufferAdd > 0 ? <span className="text-[10px] text-amber-700 ml-0.5" title={`Raw ${Math.ceil(rivetsRaw)} + ${rivetBufferAdd} (${rivetBufferPct}% buffer)`}>(+{rivetBufferAdd})</span> : null}
          {' · '}
          <strong className="font-mono-num text-amber-900" data-testid="spouting-silicone-total">{totalSilicone}</strong>
          {' '}silicone tube{totalSilicone === 1 ? '' : 's'}{siliconeNudge > 0 ? <span className="text-[10px] text-amber-700 ml-0.5" title={`Raw ${siliconeRaw.toFixed(2)} → ceil ${Math.ceil(siliconeRaw)} + 1 nudge (frac ${(siliconeRaw - Math.floor(siliconeRaw)).toFixed(2)} ≥ ${(1 - siliconeBufferPct / 100).toFixed(2)})`}>(+1 nudge)</span> : null}
        </span>
      </div>

      {/* Iter150-D — Editable consumable rates panel. Inline edits PATCH
          the appraisal's gutter-builder config so the office can tune
          per-supplier without leaving the appraisal. */}
      {showRateEditor && (
        <div data-testid="spouting-consum-rate-editor" className="border-t border-amber-200 bg-white px-3 py-2 grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
          <div className="flex flex-col gap-1">
            <div className="text-[9px] font-bold uppercase tracking-wider text-amber-900">Per dropper</div>
            <div className="flex items-center gap-1.5">
              <input
                type="number" step="1" min="0" max="50"
                data-testid="spouting-consum-edit-dropper-rivets"
                value={cnsDropperR}
                disabled={savingRates}
                onChange={(e) => onConfigPatched?.({ dropper_rivets: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                onBlur={(e) => patchRate('dropper_rivets', Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="w-14 px-1.5 py-0.5 border border-slate-300 rounded text-right font-mono-num"
              />
              <span className="text-slate-700">rivets ·</span>
              <input
                type="number" step="0.5" min="0" max="100"
                data-testid="spouting-consum-edit-dropper-pct"
                value={cnsDropperPct}
                disabled={savingRates}
                onChange={(e) => onConfigPatched?.({ dropper_silicone_pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                onBlur={(e) => patchRate('dropper_silicone_pct', Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                className="w-16 px-1.5 py-0.5 border border-slate-300 rounded text-right font-mono-num"
              />
              <span className="text-slate-700">% silicone</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-[9px] font-bold uppercase tracking-wider text-cyan-900">Per pair of mitres</div>
            <div className="flex items-center gap-1.5">
              <input
                type="number" step="1" min="0" max="50"
                data-testid="spouting-consum-edit-mitre-pair-rivets"
                value={cnsMitrePairR}
                disabled={savingRates}
                onChange={(e) => onConfigPatched?.({ mitre_pair_rivets: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                onBlur={(e) => patchRate('mitre_pair_rivets', Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="w-14 px-1.5 py-0.5 border border-slate-300 rounded text-right font-mono-num"
              />
              <span className="text-slate-700">rivets ·</span>
              <input
                type="number" step="0.5" min="0" max="100"
                data-testid="spouting-consum-edit-mitre-pair-pct"
                value={cnsMitrePairPct}
                disabled={savingRates}
                onChange={(e) => onConfigPatched?.({ mitre_pair_silicone_pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                onBlur={(e) => patchRate('mitre_pair_silicone_pct', Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                className="w-16 px-1.5 py-0.5 border border-slate-300 rounded text-right font-mono-num"
              />
              <span className="text-slate-700">% silicone</span>
            </div>
            <div className="text-[9px] text-slate-400 italic">(= {perMitreR}r + {perMitrePct}% per mitre)</div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-[9px] font-bold uppercase tracking-wider text-indigo-900">Per stop end</div>
            <div className="flex items-center gap-1.5">
              <input
                type="number" step="1" min="0" max="50"
                data-testid="spouting-consum-edit-stop-end-rivets"
                value={cnsStopEndR}
                disabled={savingRates}
                onChange={(e) => onConfigPatched?.({ stop_end_rivets: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                onBlur={(e) => patchRate('stop_end_rivets', Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="w-14 px-1.5 py-0.5 border border-slate-300 rounded text-right font-mono-num"
              />
              <span className="text-slate-700">rivets ·</span>
              <input
                type="number" step="0.5" min="0" max="100"
                data-testid="spouting-consum-edit-stop-end-pct"
                value={cnsStopEndPct}
                disabled={savingRates}
                onChange={(e) => onConfigPatched?.({ stop_end_silicone_pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                onBlur={(e) => patchRate('stop_end_silicone_pct', Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                className="w-16 px-1.5 py-0.5 border border-slate-300 rounded text-right font-mono-num"
              />
              <span className="text-slate-700">% silicone</span>
            </div>
          </div>
          {/* Iter155 — Buffer percentages applied to rivet + silicone
              push totals. Rivets multiply by (1+pct); silicone gets a
              "next-tube nudge" when the fractional remainder is within
              the buffer threshold (i.e. close to a full tube). */}
          <div className="md:col-span-3 border-t border-slate-200 pt-2 mt-1 flex flex-wrap items-center gap-3 text-[11px]">
            <span className="font-bold uppercase tracking-wider text-slate-500">Push to BOM buffers</span>
            <div className="inline-flex items-center gap-1">
              <span className="text-slate-700">Rivets</span>
              <input
                type="number" step="1" min="0" max="50"
                data-testid="spouting-consum-edit-rivet-buffer-pct"
                value={rivetBufferPct}
                disabled={savingRates}
                onChange={(e) => onConfigPatched?.({ rivet_buffer_pct: Math.max(0, Math.min(50, parseFloat(e.target.value) || 0)) })}
                onBlur={(e) => patchRate('rivet_buffer_pct', Math.max(0, Math.min(50, parseFloat(e.target.value) || 0)))}
                className="w-14 px-1.5 py-0.5 border border-slate-300 rounded text-right font-mono-num"
              />
              <span className="text-slate-700">% buffer · ceil(raw × 1+x)</span>
            </div>
            <div className="inline-flex items-center gap-1">
              <span className="text-slate-700">Silicone</span>
              <input
                type="number" step="1" min="0" max="50"
                data-testid="spouting-consum-edit-silicone-buffer-pct"
                value={siliconeBufferPct}
                disabled={savingRates}
                onChange={(e) => onConfigPatched?.({ silicone_buffer_pct: Math.max(0, Math.min(50, parseFloat(e.target.value) || 0)) })}
                onBlur={(e) => patchRate('silicone_buffer_pct', Math.max(0, Math.min(50, parseFloat(e.target.value) || 0)))}
                className="w-14 px-1.5 py-0.5 border border-slate-300 rounded text-right font-mono-num"
              />
              <span className="text-slate-700">% buffer · next-tube nudge when frac ≥ 1−x</span>
            </div>
          </div>
          <div className="md:col-span-3 text-[10px] text-slate-500">
            Saved to this appraisal&apos;s gutter-builder config. Click outside any field (Tab/blur) to persist.{savingRates ? ' · saving…' : ''}
          </div>
        </div>
      )}
      {!bracketRate && (
        <div className="px-3 py-1.5 border-t border-cyan-200 bg-amber-50 text-[10px] text-amber-800">
          ⚠ No <strong>Bracket</strong> rate row found in the Spouting catalogue. Add one (e.g. &quot;Bracket Quarter Round Plain&quot;) in <em>/component-rates</em> before pushing to BOM.
        </div>
      )}
      {bracketRate && (
        <div className="px-3 py-1.5 border-t border-cyan-100 text-[10px] text-slate-500">
          Push to BOM creates / replaces Spouting lines tagged <code className="text-cyan-700">[AUTO:bracket]</code> · <code className="text-cyan-700">[AUTO:dropper]</code> · <code className="text-cyan-700">[AUTO:stop_end]</code> · <code className="text-cyan-700">[AUTO:mitre]</code>, and Misc lines tagged <code className="text-cyan-700">[AUTO:rivet]</code> · <code className="text-cyan-700">[AUTO:silicone_modified]</code> · <code className="text-cyan-700">[AUTO:silicone_clear]</code> (Iter155). Each push is idempotent — re-clicking replaces previous auto-rows.
        </div>
      )}
    </div>
  );
}
